import type { FlowMetadata } from '../types/flow';
import { isWorkflowReadOnly, canPerformWorkflowOperation } from './workflowAccess';

export type ConstructionPathProjection = {
  workflow_path?: { logical_stages?: Array<{ key: string }> };
  workflow_progress?: { completed_keys?: string[] };
};

export type ActiveFlowRouteMetadata = ConstructionPathProjection & Pick<Partial<FlowMetadata>, 'can_edit' | 'can_share' | 'can_delete' | 'can_duplicate' | 'can_archive' | 'can_restore' | 'can_organize' | 'capabilities' | 'is_owner'> & {
  workflow_type?: string;
  standalone_mode?: boolean;
  sow_origin?: string;
  pws_origin?: string;
  market_research_view?: string;
  read_only?: boolean;
  read_only_reason?: 'similar_requirements' | 'super_admin_view' | 'training' | null;
  workflow_path?: ConstructionPathProjection['workflow_path'] & {
    path_id?: string;
    version?: number;
    policy_version?: number;
    applicable_keys?: string[];
    terminal_key?: string;
  };
};

/** Only route-shaping server data belongs in this comparison; progress changes as users work. */
export const activeFlowRouteSignature = (metadata: ActiveFlowRouteMetadata): string => JSON.stringify({
  workflowType: metadata.workflow_type ?? 'market_research',
  standalone: metadata.standalone_mode === true,
  sowOrigin: metadata.sow_origin ?? null,
  pwsOrigin: metadata.pws_origin ?? null,
  marketResearchView: metadata.market_research_view ?? null,
  readOnly: isWorkflowReadOnly(metadata),
  operations: Object.fromEntries((['share', 'delete', 'duplicate', 'archive', 'restore', 'organize'] as const)
    .map(operation => [operation, canPerformWorkflowOperation(metadata, operation)])),
  readOnlyReason: metadata.read_only_reason ?? null,
  path: metadata.workflow_path ? {
    id: metadata.workflow_path.path_id ?? null,
    version: metadata.workflow_path.version ?? null,
    policyVersion: metadata.workflow_path.policy_version ?? null,
    stages: metadata.workflow_path.logical_stages ?? null,
    terminal: metadata.workflow_path.terminal_key ?? null,
  } : null,
});

/** Use the caller-specific path returned by the server, including in role previews. */
export const constructionPathKeys = (metadata: ConstructionPathProjection | null | undefined): string[] =>
  metadata?.workflow_path?.logical_stages?.map((stage) => stage.key) ?? [];

export const constructionHasStage = (
  metadata: ConstructionPathProjection | null | undefined,
  key: string,
): boolean => constructionPathKeys(metadata).includes(key);

/** The path projection determines both the successor and the terminal page. */
export const resumeOneTimeConstructionProjectedStage = (
  metadata: ConstructionPathProjection,
  verificationReady: boolean,
): number => {
  const keys = constructionPathKeys(metadata);
  if (keys.length === 0) return 0;
  const completedKeys = new Set(metadata.workflow_progress?.completed_keys ?? []);
  const lastCompleted = keys.reduce((last, key, index) => completedKeys.has(key) ? index : last, -1);
  if (!verificationReady && lastCompleted < keys.indexOf('manual_edit')) return 0;
  return Math.min(keys.length - 1, lastCompleted + 1);
};
